import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";

import {
  apiClient,
  type DepartmentSummary,
  type TemporaryCredential,
  type UserRole,
} from "../../api/client";
import { PasswordInput } from "../../components/PasswordInput";
import { errorMessage } from "../admin/errors";

interface AddUserDialogProps {
  actorRole: "manager" | "owner";
  departments: DepartmentSummary[];
  onClose: () => void;
  onCreated: () => void;
}

export function AddUserDialog({ actorRole, departments, onClose, onCreated }: AddUserDialogProps) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [role, setRole] = useState<UserRole>("user");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [result, setResult] = useState<TemporaryCredential | null>(null);
  const [copyStatus, setCopyStatus] = useState("");
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const dialog = useRef<HTMLElement>(null);

  const generatePassword = useCallback(async (usernameHint?: string) => {
    setGenerating(true);
    setError("");
    setCopyStatus("");
    try {
      setTemporaryPassword(await apiClient.generateTemporaryPassword(usernameHint));
    } catch (caught) {
      setTemporaryPassword("");
      setError(errorMessage(caught));
    } finally {
      setGenerating(false);
    }
  }, []);

  useEffect(() => {
    void generatePassword();
  }, [generatePassword]);

  useEffect(() => {
    dialog.current?.querySelector<HTMLElement>("[data-initial-focus]")?.focus();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!temporaryPassword) return;
    setSubmitting(true);
    setError("");
    setCopyStatus("");
    try {
      const created = await apiClient.createUser({
        username: username.trim(),
        display_name: displayName.trim(),
        temporary_password: temporaryPassword,
        role,
        primary_department_id: departmentId || null,
      });
      setTemporaryPassword("");
      setResult(created);
      onCreated();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function copyPassword(password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setCopyStatus("临时密码已复制。关闭后将不再显示。");
    } catch {
      setCopyStatus("浏览器无法复制，请在关闭前手动保存临时密码。");
    }
  }

  const secret = result?.temporary_password ?? temporaryPassword;
  const departmentRequired = role !== "owner";

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialog}
        aria-labelledby="add-user-title"
        aria-modal="true"
        className="modal-card user-create-dialog"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !submitting) onClose();
        }}
        role="dialog"
      >
        <div className="panel-heading">
          <div>
            <span className="eyebrow">独立创建流程</span>
            <h2 id="add-user-title">添加用户</h2>
          </div>
          <button
            aria-label="关闭添加用户"
            className="text-button"
            disabled={submitting}
            onClick={onClose}
            type="button"
          >
            关闭
          </button>
        </div>

        {result ? (
          <CreationResult
            copyStatus={copyStatus}
            onClose={onClose}
            onCopy={() => void copyPassword(result.temporary_password)}
            result={result}
          />
        ) : (
          <form className="user-create-form" onSubmit={(event) => void submit(event)}>
            <label>
              用户名
              <input
                autoComplete="off"
                data-initial-focus
                maxLength={64}
                onChange={(event) => setUsername(event.target.value)}
                required
                value={username}
              />
            </label>
            <label>
              显示名称
              <input
                maxLength={160}
                onChange={(event) => setDisplayName(event.target.value)}
                required
                value={displayName}
              />
            </label>
            <label>
              初始角色
              <select onChange={(event) => setRole(event.target.value as UserRole)} value={role}>
                <option value="user">user</option>
                {actorRole === "owner" && <option value="manager">manager</option>}
                {actorRole === "owner" && <option value="owner">owner</option>}
              </select>
            </label>
            <label>
              主部门{departmentRequired ? "（必选）" : "（可选）"}
              <select
                onChange={(event) => setDepartmentId(event.target.value)}
                required={departmentRequired}
                value={departmentId}
              >
                <option value="">请选择部门</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name} · {department.code}
                  </option>
                ))}
              </select>
            </label>
            <div className="user-generated-password">
              <label htmlFor="generated-temporary-password">临时密码</label>
              <PasswordInput
                aria-describedby="temporary-password-note"
                disabled={generating}
                hideLabel="隐藏生成的临时密码"
                id="generated-temporary-password"
                readOnly
                showLabel="显示生成的临时密码"
                value={generating ? "" : secret}
              />
              <p id="temporary-password-note">
                {generating ? "正在生成新密码…" : "只会在本次创建流程中显示，请安全交付。"}
              </p>
              <div className="user-secret-actions">
                <button
                  disabled={generating || !secret}
                  onClick={() => void copyPassword(secret)}
                  type="button"
                >
                  复制临时密码
                </button>
                <button
                  disabled={generating || submitting}
                  onClick={() => void generatePassword(username.trim() || undefined)}
                  type="button"
                >
                  重新生成
                </button>
              </div>
            </div>
            {copyStatus && (
              <p className="status-banner" role="status">
                {copyStatus}
              </p>
            )}
            {error && (
              <p className="alert error" role="alert">
                {error}
              </p>
            )}
            <div className="modal-actions">
              <button disabled={submitting} onClick={onClose} type="button">
                取消
              </button>
              <button disabled={submitting || generating || !temporaryPassword} type="submit">
                {submitting ? "正在创建…" : "创建账号"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}

function CreationResult({
  result,
  copyStatus,
  onCopy,
  onClose,
}: {
  result: TemporaryCredential;
  copyStatus: string;
  onCopy: () => void;
  onClose: () => void;
}) {
  return (
    <div className="user-creation-result">
      <p className="status-banner" role="status">
        已创建 {result.display_name}。请立即保存并安全交付以下一次性临时密码。
      </p>
      <dl>
        <div>
          <dt>用户名</dt>
          <dd>{result.username}</dd>
        </div>
        <div>
          <dt>有效期至</dt>
          <dd>{new Date(result.temporary_password_expires_at).toLocaleString()}</dd>
        </div>
      </dl>
      <div className="user-generated-password">
        <label htmlFor="created-temporary-password">一次性临时密码</label>
        <PasswordInput
          hideLabel="隐藏创建结果临时密码"
          id="created-temporary-password"
          readOnly
          showLabel="显示创建结果临时密码"
          value={result.temporary_password}
        />
        <button onClick={onCopy} type="button">
          复制临时密码
        </button>
      </div>
      {copyStatus && (
        <p className="status-banner" role="status">
          {copyStatus}
        </p>
      )}
      <div className="modal-actions">
        <button className="primary" onClick={onClose} type="button">
          我已保存，关闭
        </button>
      </div>
    </div>
  );
}
